/**
 * PriceAlertsBanner — Dashboard-level warning banner for products whose
 * price rose ≥ threshold_pct this year. Extracted from Dashboard.jsx in
 * iter 60 to keep the parent under 1000 lines.
 *
 * Behavior preserved bit-for-bit: click product name → deep-link to
 * /products?q=<name>&expand=1; Accept dismisses for the year; Review both
 * dismisses AND navigates to /products for PVP editing.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export default function PriceAlertsBanner({ alerts, lang, formatMoney, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null);
  const navigate = useNavigate();
  if (!alerts || !alerts.count) return null;
  const products = alerts.products || [];
  const preview = products.slice(0, 4);
  const rest = Math.max(0, products.length - preview.length);

  const handleDismiss = async (p, decision) => {
    setBusy(p.product_name);
    try {
      await api.post("/stats/price-alerts/dismiss", {
        product_name: p.product_name,
        year: alerts.year,
        decision,
      });
      onDismiss?.();
      if (decision === "reviewed") {
        navigate(`/products?q=${encodeURIComponent(p.product_name)}`);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      data-testid="price-alerts-banner"
      className="rounded-lg border-2 border-[var(--status-warning)] bg-[var(--status-warning-bg)] p-4 flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle size={22} className="text-[var(--status-warning)] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="font-display font-semibold text-[var(--status-warning)]">
            {lang === "es"
              ? `Aviso: ${alerts.count} producto${alerts.count === 1 ? "" : "s"} con subidas ≥ ${alerts.threshold_pct}% este año`
              : `Warning: ${alerts.count} product${alerts.count === 1 ? "" : "s"} up ≥ ${alerts.threshold_pct}% this year`}
          </p>
          <p className="text-xs text-[var(--text-secondary)] mt-1">
            {lang === "es"
              ? "Importante revisar PVP en los platos afectados y actualizar los escandallos."
              : "Review dish PVP and refresh the affected escandallos."}
          </p>
        </div>
        {products.length > preview.length && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            data-testid="price-alerts-expand"
            className="text-xs text-[var(--primary)] hover:underline shrink-0 self-start"
          >
            {expanded
              ? (lang === "es" ? "Ver menos" : "Show less")
              : (lang === "es" ? `Ver todos (${products.length})` : `Show all (${products.length})`)}
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
        {(expanded ? products : preview).map((p) => (
          <div
            key={p.product_name}
            data-testid={`price-alert-row-${p.product_name}`}
            className="flex items-center justify-between p-2 rounded bg-[var(--surface)] border border-[var(--border-color)]/60"
          >
            <button
              type="button"
              onClick={() => navigate(`/products?q=${encodeURIComponent(p.product_name)}&expand=1`)}
              data-testid={`price-alert-open-${p.product_name}`}
              title={lang === "es" ? "Abrir evolución del producto" : "Open product evolution"}
              className="min-w-0 flex-1 text-left hover:underline focus:outline-none focus:ring-2 focus:ring-[var(--primary)]/40 rounded"
            >
              <p className="font-medium truncate capitalize text-[var(--primary)]">{p.product_name}</p>
              <p className="text-[10px] text-[var(--text-muted)]">
                {formatMoney(p.first_price, p.currency)} → {formatMoney(p.last_price, p.currency)}
                {p.vendor && ` · ${p.vendor}`}
              </p>
            </button>
            <span className="font-mono-num font-semibold text-[var(--status-warning)] shrink-0 ml-2">
              +{p.change_pct.toFixed(1)}%
            </span>
            <div className="flex items-center gap-1 shrink-0 ml-2">
              <button
                type="button"
                onClick={() => handleDismiss(p, "accepted")}
                disabled={busy === p.product_name}
                data-testid={`price-alert-accept-${p.product_name}`}
                className="px-2 py-1 rounded text-[10px] font-medium border border-[var(--border-color)] bg-[var(--surface-hover)] hover:bg-[var(--surface)] disabled:opacity-40"
                title={lang === "es" ? "Aceptar la subida — ocultar aviso este año" : "Accept — hide alert for this year"}
              >
                {lang === "es" ? "Aceptar" : "Accept"}
              </button>
              <button
                type="button"
                onClick={() => handleDismiss(p, "reviewed")}
                disabled={busy === p.product_name}
                data-testid={`price-alert-review-${p.product_name}`}
                className="px-2 py-1 rounded text-[10px] font-medium bg-[var(--primary)] text-white hover:bg-[var(--primary-hover)] disabled:opacity-40"
                title={lang === "es" ? "Ir a Productos para revisar PVP + escandallo" : "Go to Products to review PVP + dish cost"}
              >
                {lang === "es" ? "Revisar" : "Review"}
              </button>
            </div>
          </div>
        ))}
        {!expanded && rest > 0 && (
          <div className="text-xs text-[var(--text-muted)] p-2">
            {lang === "es" ? `+ ${rest} más...` : `+ ${rest} more...`}
          </div>
        )}
      </div>
    </div>
  );
}
